// Design tokens — the single source of truth for the UI.
// Nothing below should be referenced directly by screens; consume them through
// the MUI theme (palette.*, shape, shadows, spacing) so light/dark both work.

export const palette = {
  // Neutral ramp. Backgrounds, borders, text.
  slate: {
    0: "#FFFFFF",
    25: "#FCFCFD",
    50: "#F8FAFC",
    100: "#F1F5F9",
    200: "#E2E8F0",
    300: "#CBD5E1",
    400: "#94A3B8",
    500: "#64748B",
    600: "#475569",
    700: "#334155",
    800: "#1E293B",
    900: "#0F172A",
    950: "#020617",
  },
  // Single accent, descended from the old steel blue but cleaner.
  blue: {
    50: "#EFF4FF",
    100: "#DBE6FE",
    200: "#BFD3FE",
    300: "#93B4FD",
    400: "#608FFA",
    500: "#3B6CF6",
    600: "#2554EB",
    700: "#1D42D8",
    800: "#1E3AAF",
    900: "#1E378A",
  },
  green: {
    50: "#ECFDF5", 100: "#D1FAE5", 400: "#34D399",
    500: "#10B981", 600: "#059669", 700: "#047857",
  },
  amber: {
    50: "#FFFBEB", 100: "#FEF3C7", 400: "#FBBF24",
    500: "#F59E0B", 600: "#D97706", 700: "#B45309",
  },
  red: {
    50: "#FEF2F2", 100: "#FEE2E2", 400: "#F87171",
    500: "#EF4444", 600: "#DC2626", 700: "#B91C1C",
  },
  // Extra accents so dashboard tiles can each carry their own hue instead of
  // every surface being blue-on-white.
  violet: {
    50: "#F3F0FF", 100: "#E7E0FF", 400: "#9B83FB",
    500: "#7C5CFA", 600: "#6A45E6", 700: "#5733C4",
  },
  teal: {
    50: "#E7FAF7", 100: "#C7F2EB", 400: "#2DD4BF",
    500: "#14B8A6", 600: "#0D9488", 700: "#0F766E",
  },
  // Brand chrome: the sidebar and the mobile header. A solid colour here is
  // what stops the app reading as one continuous white sheet.
  navy: {
    700: "#1E2F6B",
    800: "#16245A",
    900: "#0F1B45",
    950: "#0A1330",
  },
};

// 4px base. theme.spacing(n) === n * 8, these are for explicit layout values.
export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  "2xl": 20,
  full: 9999,
};

export const layout = {
  sidebarWidth: 256,
  sidebarCollapsedWidth: 72,
  topBarHeight: 60,
  bottomNavHeight: 60,
  // Below this width the app switches to the mobile app-like shell.
  mobileBreakpoint: "md",
  contentMaxWidth: 1400,
};

// Soft, layered shadows. Dark mode uses deeper/blacker variants.
export const shadowsLight = {
  xs: "0 1px 2px 0 rgba(15, 23, 42, 0.04)",
  sm: "0 1px 3px 0 rgba(15, 23, 42, 0.06), 0 1px 2px -1px rgba(15, 23, 42, 0.04)",
  md: "0 4px 12px -2px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.04)",
  lg: "0 12px 24px -6px rgba(15, 23, 42, 0.10), 0 4px 8px -4px rgba(15, 23, 42, 0.05)",
  xl: "0 24px 48px -12px rgba(15, 23, 42, 0.18)",
};

export const shadowsDark = {
  xs: "0 1px 2px 0 rgba(0, 0, 0, 0.30)",
  sm: "0 1px 3px 0 rgba(0, 0, 0, 0.40), 0 1px 2px -1px rgba(0, 0, 0, 0.30)",
  md: "0 4px 12px -2px rgba(0, 0, 0, 0.45), 0 2px 4px -2px rgba(0, 0, 0, 0.30)",
  lg: "0 12px 24px -6px rgba(0, 0, 0, 0.50), 0 4px 8px -4px rgba(0, 0, 0, 0.35)",
  xl: "0 24px 48px -12px rgba(0, 0, 0, 0.60)",
};

export const fontStack =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
